import {
  getAnalyticsSummary,
  getAnalyticsQuestions,
  getAnalyticsTrends,
  getAnalyticsTopQuestions,
  getAnalyticsPeakHours,
} from '../api/client.js';

let summaryGrid, secondaryGrid, questionsList, fromInput, toInput, audienceSelect, refreshBtn;
let trendsChart, peakChart, topQuestionsList;

export function initAnalytics() {
  summaryGrid = document.getElementById('analyticsSummary');
  secondaryGrid = document.getElementById('analyticsSecondary');
  questionsList = document.getElementById('analyticsQuestionsList');
  fromInput = document.getElementById('analyticsFrom');
  toInput = document.getElementById('analyticsTo');
  audienceSelect = document.getElementById('analyticsAudience');
  refreshBtn = document.getElementById('analyticsRefreshBtn');
  trendsChart = document.getElementById('analyticsTrendsChart');
  peakChart = document.getElementById('analyticsPeakChart');
  topQuestionsList = document.getElementById('analyticsTopQuestionsList');

  if (!refreshBtn) return;

  // Default date range: last 30 days
  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  fromInput.value = thirtyDaysAgo.toISOString().split('T')[0];
  toInput.value = today.toISOString().split('T')[0];

  refreshBtn.addEventListener('click', () => loadAnalytics());
}

export async function loadAnalytics() {
  if (!summaryGrid) return;

  const fromDate = fromInput.value || null;
  const toDate = toInput.value ? toInput.value + 'T23:59:59' : null;
  const audience = audienceSelect.value || null;

  const spinner = '<div class="analytics-loading"><div class="spinner"></div></div>';
  summaryGrid.innerHTML = spinner;
  if (secondaryGrid) secondaryGrid.innerHTML = '';
  questionsList.innerHTML = spinner;
  if (trendsChart) trendsChart.innerHTML = spinner;
  if (peakChart) peakChart.innerHTML = spinner;
  if (topQuestionsList) topQuestionsList.innerHTML = spinner;

  try {
    const [summary, questions, trends, topQuestions, peakHours] = await Promise.all([
      getAnalyticsSummary(fromDate, toDate, audience),
      getAnalyticsQuestions(fromDate, toDate, audience),
      getAnalyticsTrends(fromDate, toDate, audience),
      getAnalyticsTopQuestions(fromDate, toDate, audience),
      getAnalyticsPeakHours(fromDate, toDate, audience),
    ]);

    renderSummary(summary);
    renderSecondaryMetrics(summary);
    renderQuestions(questions.questions, questions.total);
    renderTrendsChart(trends);
    renderTopQuestions(topQuestions);
    renderPeakHours(peakHours);
  } catch (error) {
    summaryGrid.innerHTML = `<div class="analytics-no-data">Fehler: ${error.message}</div>`;
    questionsList.innerHTML = '';
  }
}

// ---------------------------------------------------------------------------
// Summary metrics (primary row — 6 cards)
// ---------------------------------------------------------------------------

function renderSummary(data) {
  const positiveFeedbackRate =
    data.positive_feedback_rate ?? data.answer_success_rate;
  const abandonRate = data.abandonment_rate;

  const metrics = [
    { label: 'Chat-Sitzungen', value: data.total_sessions, cls: 'metric-neutral' },
    { label: 'Gestellte Fragen', value: data.total_questions, cls: 'metric-neutral' },
    { label: 'Positives Feedback', value: data.feedback_positive, cls: 'metric-positive' },
    { label: 'Negatives Feedback', value: data.feedback_negative, cls: 'metric-negative' },
    {
      label: 'Pos. Feedback-Quote',
      value: positiveFeedbackRate != null ? positiveFeedbackRate + '%' : '--',
      cls: positiveFeedbackRate == null ? 'metric-neutral' : positiveFeedbackRate >= 70 ? 'metric-positive' : positiveFeedbackRate >= 40 ? 'metric-warn' : 'metric-negative',
      title: 'Anteil positiver Bewertungen an allen abgegebenen Bewertungen (nicht an allen Fragen)',
    },
    {
      label: 'Abbruchrate',
      value: abandonRate != null ? abandonRate + '%' : '--',
      cls: abandonRate == null ? 'metric-neutral' : abandonRate <= 20 ? 'metric-positive' : abandonRate <= 50 ? 'metric-warn' : 'metric-negative',
    },
  ];

  summaryGrid.innerHTML = metrics
    .map(
      (m) => `
    <div class="analytics-card"${m.title ? ` title="${m.title}"` : ''}>
      <div class="metric-value ${m.cls}">${m.value}</div>
      <div class="metric-label">${m.label}</div>
    </div>
  `
    )
    .join('');
}

// ---------------------------------------------------------------------------
// Secondary metrics (4 new cards)
// ---------------------------------------------------------------------------

function renderSecondaryMetrics(data) {
  if (!secondaryGrid) return;

  const fbRate = data.feedback_rate;

  const metrics = [
    {
      label: 'Avg. Antwortlänge',
      value: data.avg_response_length != null ? data.avg_response_length.toLocaleString('de-DE') + ' Zeichen' : '--',
      cls: 'metric-neutral',
    },
    {
      label: 'Avg. Sitzungsdauer',
      value: data.avg_session_duration_minutes != null ? data.avg_session_duration_minutes + ' Min.' : '--',
      cls: 'metric-neutral',
    },
    {
      label: 'Fragen / Sitzung',
      value: data.avg_questions_per_session != null ? data.avg_questions_per_session : '--',
      cls: 'metric-neutral',
    },
    {
      label: 'Feedback-Quote',
      value: fbRate != null ? fbRate + '%' : '--',
      cls: fbRate == null ? 'metric-neutral' : fbRate >= 30 ? 'metric-positive' : fbRate >= 10 ? 'metric-warn' : 'metric-negative',
    },
  ];

  secondaryGrid.innerHTML = metrics
    .map(
      (m) => `
    <div class="analytics-card analytics-card-secondary">
      <div class="metric-value ${m.cls}">${m.value}</div>
      <div class="metric-label">${m.label}</div>
    </div>
  `
    )
    .join('');
}

// ---------------------------------------------------------------------------
// Daily trends — vertical bar chart
// ---------------------------------------------------------------------------

function renderTrendsChart(trends) {
  if (!trendsChart) return;

  if (!trends || trends.length === 0) {
    trendsChart.innerHTML = '<div class="analytics-no-data">Keine Daten im gewählten Zeitraum.</div>';
    return;
  }

  const displayData = trends.length > 30 ? trends.slice(-30) : trends;
  const maxQuestions = Math.max(...displayData.map((d) => d.questions), 1);

  const bars = displayData
    .map((d) => {
      const heightPct = Math.max((d.questions / maxQuestions) * 100, 2);
      const dateLabel = formatDateShort(d.date);
      return `
        <div class="trends-bar-col" title="${dateLabel}: ${d.questions} Fragen, ${d.sessions} Sitzungen">
          <div class="trends-bar-value">${d.questions}</div>
          <div class="trends-bar" style="height: ${heightPct}%"></div>
          <div class="trends-bar-label">${dateLabel}</div>
        </div>
      `;
    })
    .join('');

  trendsChart.innerHTML = `
    <div class="trends-legend">
      <span class="trends-legend-item"><span class="trends-legend-dot trends-dot-questions"></span>Fragen pro Tag</span>
    </div>
    <div class="trends-chart-grid" style="grid-template-columns: repeat(${displayData.length}, 1fr)">
      ${bars}
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Top questions — numbered list with count badges
// ---------------------------------------------------------------------------

function renderTopQuestions(topQuestions) {
  if (!topQuestionsList) return;

  const heading = document.querySelector('#analyticsTopQuestionsCard h2');

  if (!topQuestions || topQuestions.length === 0) {
    if (heading) heading.textContent = 'Häufigste Fragen';
    topQuestionsList.innerHTML =
      '<div class="analytics-no-data">Keine Fragen im gewählten Zeitraum.</div>';
    return;
  }

  if (heading) {
    heading.textContent = `Häufigste Fragen (Top ${topQuestions.length})`;
  }

  topQuestionsList.innerHTML = topQuestions
    .map(
      (q, i) => `
    <div class="analytics-question-row analytics-top-question-row">
      <div class="analytics-question-main">
        <span class="analytics-top-rank">${i + 1}.</span>
        <span class="analytics-question-text">${escapeHtml(q.question_text)}</span>
      </div>
      <div class="analytics-top-meta">
        <span class="analytics-top-count-badge">${q.count}x</span>
        <span class="analytics-question-meta">Zuletzt: ${formatDate(q.last_asked)}</span>
      </div>
    </div>
  `
    )
    .join('');
}

// ---------------------------------------------------------------------------
// Peak hours — horizontal bar chart (24 rows)
// ---------------------------------------------------------------------------

function renderPeakHours(peakHours) {
  if (!peakChart) return;

  if (!peakHours || peakHours.length === 0) {
    peakChart.innerHTML = '<div class="analytics-no-data">Keine Daten im gewählten Zeitraum.</div>';
    return;
  }

  const maxCount = Math.max(...peakHours.map((h) => h.count), 1);

  peakChart.innerHTML = peakHours
    .map((h) => {
      const widthPct = (h.count / maxCount) * 100;
      const hourLabel = String(h.hour).padStart(2, '0') + ':00';
      return `
        <div class="peak-row" title="${hourLabel}: ${h.count} Interaktionen">
          <span class="peak-hour-label">${hourLabel}</span>
          <div class="peak-bar-track">
            <div class="peak-bar" style="width: ${widthPct}%"></div>
          </div>
          <span class="peak-count">${h.count}</span>
        </div>
      `;
    })
    .join('');
}

// ---------------------------------------------------------------------------
// Negative questions list (existing)
// ---------------------------------------------------------------------------

const AUDIENCE_LABELS = {
  end_user: 'Endkunden',
  authority: 'Behörden',
};

function translateAudience(raw) {
  return AUDIENCE_LABELS[raw] || raw || 'Unbekannt';
}

function updateQuestionHeading(count) {
  // Target the negative-questions heading specifically (not top questions)
  const cards = document.querySelectorAll('.analytics-questions-card:not(#analyticsTopQuestionsCard) h2');
  const heading = cards.length > 0 ? cards[0] : null;
  if (heading) {
    heading.textContent = count > 0
      ? `Negativ bewertete Fragen (${count})`
      : 'Negativ bewertete Fragen';
  }
}

function renderQuestions(questions, total) {
  updateQuestionHeading(total || (questions ? questions.length : 0));

  if (!questions || questions.length === 0) {
    questionsList.innerHTML =
      '<div class="analytics-no-data analytics-empty-state">' +
      '<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="margin-bottom:8px;opacity:0.5">' +
      '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>' +
      '<p>Keine negativ bewerteten Fragen im gewählten Zeitraum.</p></div>';
    return;
  }

  questionsList.innerHTML = questions
    .map(
      (q) => `
    <div class="analytics-question-row">
      <div class="analytics-question-main">
        <span class="analytics-question-text">${escapeHtml(q.question_text)}</span>
        ${q.comment ? `<div class="analytics-question-comment">${escapeHtml(q.comment)}</div>` : ''}
      </div>
      <span class="analytics-question-meta">${escapeHtml(translateAudience(q.audience))} &middot; ${formatDate(q.created_at)}</span>
    </div>
  `
    )
    .join('');
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function formatDate(isoString) {
  try {
    return new Date(isoString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return isoString;
  }
}

function formatDateShort(isoDate) {
  try {
    const d = new Date(isoDate + 'T00:00:00');
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  } catch {
    return isoDate;
  }
}
